// The public sub-processor list (privacy notice section 9, docs/legal/sub-processors.md), at /sub-processors for
// anyone, signed in or not. It names every third party that processes personal data for LAMP's hosted service,
// what it receives and where. Entries still being confirmed say so.
import { CATALOGUES, useWords, type Words } from "../i18n/words.js";

export interface SubProcessor {
  readonly provider: string;
  readonly purpose: string;
  readonly receives: string;
  readonly country: string;
  /** Whether it processes the business's data, or only LAMP's own (sign-in, billing). */
  readonly tenantData: "yes" | "when enabled" | "LAMP only";
  readonly unconfirmed?: boolean;
}

/** The list in `w`'s language. Providers' names stay as they are registered. */
export function subProcessors(
  w: Words = CATALOGUES.en,
): readonly { readonly group: string; readonly entries: readonly SubProcessor[] }[] {
  return [
    {
      group: w.subProcessorsGroupHosting,
      entries: [
        {
          provider: "Amazon Web Services India Pvt. Ltd.",
          purpose: w.subProcessorsAwsPurpose,
          receives: w.subProcessorsAwsReceives,
          country: w.subProcessorsAwsCountry,
          tenantData: "yes",
          unconfirmed: true,
        },
      ],
    },
    {
      group: w.subProcessorsGroupModels,
      entries: [
        {
          provider: "Anthropic, PBC",
          purpose: w.subProcessorsAnthropicPurpose,
          receives: w.subProcessorsAnthropicReceives,
          country: w.subProcessorsUnitedStates,
          tenantData: "yes",
          unconfirmed: true,
        },
        {
          provider: w.subProcessorsOpenRouterProvider,
          purpose: w.subProcessorsOpenRouterPurpose,
          receives: w.subProcessorsMaskedReceives,
          country: w.subProcessorsUsEu,
          tenantData: "yes",
          unconfirmed: true,
        },
        {
          provider: "OpenAI, L.L.C.",
          purpose: w.subProcessorsAlternativePurpose,
          receives: w.subProcessorsMaskedReceives,
          country: w.subProcessorsUnitedStates,
          tenantData: "yes",
          unconfirmed: true,
        },
        {
          provider: "Google LLC (Gemini API)",
          purpose: w.subProcessorsAlternativePurpose,
          receives: w.subProcessorsMaskedReceives,
          country: w.subProcessorsUnitedStates,
          tenantData: "yes",
          unconfirmed: true,
        },
      ],
    },
    {
      group: w.subProcessorsGroupMessaging,
      entries: [
        {
          provider: "Telegram",
          purpose: w.subProcessorsTelegramPurpose,
          receives: w.subProcessorsTelegramReceives,
          country: w.subProcessorsOutsideIndia,
          tenantData: "yes",
          unconfirmed: true,
        },
        {
          provider: "Meta Platforms (WhatsApp Business Platform)",
          purpose: w.subProcessorsWhatsAppPurpose,
          receives: w.subProcessorsWhatsAppReceives,
          country: w.subProcessorsWhatsAppCountry,
          tenantData: "when enabled",
          unconfirmed: true,
        },
      ],
    },
    {
      group: w.subProcessorsGroupEmail,
      entries: [
        {
          provider: "Amazon Web Services (Amazon SES)",
          purpose: w.subProcessorsSesPurpose,
          receives: w.subProcessorsSesReceives,
          country: w.subProcessorsIndia,
          tenantData: "yes",
          unconfirmed: true,
        },
        {
          provider: w.subProcessorsSmsProvider,
          purpose: w.subProcessorsSmsPurpose,
          receives: w.subProcessorsSmsReceives,
          country: w.subProcessorsIndia,
          tenantData: "LAMP only",
          unconfirmed: true,
        },
      ],
    },
    {
      group: w.subProcessorsGroupPayments,
      entries: [
        {
          provider: "Razorpay Software Pvt. Ltd.",
          purpose: w.subProcessorsRazorpayPurpose,
          receives: w.subProcessorsRazorpayReceives,
          country: w.subProcessorsIndia,
          tenantData: "LAMP only",
        },
      ],
    },
  ];
}

/** The list in English. */
export const SUB_PROCESSORS = subProcessors();

const tenantData = (w: Words, value: SubProcessor["tenantData"]): string =>
  ({
    yes: w.subProcessorsTenantDataYes,
    "when enabled": w.subProcessorsTenantDataWhenEnabled,
    "LAMP only": w.subProcessorsTenantDataLampOnly,
  })[value];

export function SubProcessors() {
  const w = useWords();
  return (
    <main id="content" className="public-page">
      <h1>{w.subProcessorsTitle}</h1>
      <p>{w.subProcessorsIntro}</p>
      {subProcessors(w).map(({ group, entries }) => (
        <section key={group} aria-label={group}>
          <h2 className="sectionh">{group}</h2>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th scope="col">{w.subProcessorsProvider}</th>
                  <th scope="col">{w.subProcessorsWhatTheyDo}</th>
                  <th scope="col">{w.subProcessorsWhatTheyReceive}</th>
                  <th scope="col">{w.subProcessorsWhere}</th>
                  <th scope="col">{w.subProcessorsBusinessData}</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.provider}>
                    <td>
                      {e.provider}
                      {e.unconfirmed ? <span className="small muted"> · {w.subProcessorsBeingConfirmed}</span> : null}
                    </td>
                    <td>{e.purpose}</td>
                    <td>{e.receives}</td>
                    <td>{e.country}</td>
                    <td>{tenantData(w, e.tenantData)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}
      <p className="muted">{w.subProcessorsNotListed}</p>
      <p>
        <a href="/">{w.subProcessorsBack}</a>
      </p>
    </main>
  );
}
